import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import Dropdown from '../ui/Dropdown';
import { useGetAddressesQuery } from '../../redux/apis';
import { selectDefaultAddress } from '../../redux/slices/addressSlice';
import { Modal } from 'semantic-ui-react';
import AddressForm from './AddressForm';

/**
 * Reusable address dropdown component 
 * Used by both AvailabilitySection and DeliveryMethodSelection components
 */
const AddressDropdown = ({
  value = '',
  onChange,
  name = 'addressSelect',
  label = 'Select Address',
  placeholder = 'Select an address',
  error = '',
  showAddNew = true,
  required = false,
  className = '',
  fluid = false,
  disabled = false
}) => {
  // State for address form modal
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [internalValue, setInternalValue] = useState(value);
  const autoSelectDone = useRef(false);

  // Fetch addresses from Redux
  const { data: addresses = [], isLoading, refetch } = useGetAddressesQuery(undefined, {
    skip: !useSelector(state => state.auth?.isAuthenticated)
  });
  const defaultAddress = useSelector(selectDefaultAddress);


  // Reset auto-selection when addresses or defaultAddress changes
  useEffect(() => {
    // Reset the autoSelectDone flag when addresses or defaultAddress changes
    if (addresses?.length > 0 || defaultAddress) {
      autoSelectDone.current = false;
    }
  }, [addresses, defaultAddress]);

  // Reset auto-selection only when defaultAddress becomes available
  useEffect(() => {
    // Only reset if we have addresses but no selection yet
    if (defaultAddress && addresses?.length > 0 && !autoSelectDone.current) {
      // Don't reset if we already have a value selected
      if (!internalValue || internalValue === '') {
      } else {
        autoSelectDone.current = true;
      }
    }
  }, [defaultAddress, addresses, internalValue]);

  // Automatically select default address if no value is provided
  useEffect(() => {
    if (addresses?.length > 0 && !autoSelectDone.current) {
      // Skip auto-selection if user has already selected a value
      if (internalValue && internalValue !== '') {
        autoSelectDone.current = true;
        return;
      }

      // Prioritize default address if available
      if (defaultAddress) {
        setInternalValue(defaultAddress.id);

        // Notify parent component about the default selection
        if (onChange) {
          const mockEvent = {
            target: {
              name,
              value: defaultAddress.id,
              _addressObject: defaultAddress
            }
          };
          onChange(mockEvent);
        }

        autoSelectDone.current = true;
        return;
      }

      // Fallback to first address if no default exists
      if (addresses[0]) {
        setInternalValue(addresses[0].id);

        // Notify parent component
        if (onChange) {
          const mockEvent = {
            target: {
              name,
              value: addresses[0].id,
              _addressObject: addresses[0]
            }
          };
          onChange(mockEvent);
        }

        autoSelectDone.current = true;
      }
    }
  }, [addresses, defaultAddress, internalValue, onChange, name]);

  // Update internal value when external value changes
  useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(value);

      // If a value is provided externally, consider auto-selection done
      if (value) {
        autoSelectDone.current = true;
      }
    }
  }, [value, internalValue]);

  // Create options from addresses
  const addressOptions = [
    { value: '', label: placeholder },
    ...(addresses?.map(address => {
      // Format the display text — short and clean
      const displayText = `${address.street}, ${address.city}, ${address.state} ${address.postalCode}`;
      // Add "(Default)" to the default address
      const isDefault = defaultAddress && defaultAddress.id === address.id;
      const text = isDefault ? `${displayText} (Default)` : displayText;

      return {
        value: address.id,
        label: text
      };
    }) || []),
    ...(showAddNew ? [{ value: 'new', label: '+ Add New Address' }] : [])
  ];

  // Handle address selection
  const handleChange = (selectedValue) => {
    // Handle both event objects (from legacy) and raw values (from custom Dropdown)
    const val = selectedValue?.target ? selectedValue.target.value : selectedValue;

    if (val === 'new') {
      // Open the address form modal
      setShowAddressModal(true);
      return;
    }

    setInternalValue(val);
    autoSelectDone.current = true; // Mark as explicitly selected

    if (onChange) {
      const selectedAddress = addresses.find(addr => addr.id === val);
      const mockEvent = {
        target: {
          name,
          value: val,
          ...(selectedAddress && { _addressObject: selectedAddress })
        }
      };
      onChange(mockEvent);
    }
  };

  // Handle new address creation
  const handleAddressSuccess = (newAddress) => {
    setShowAddressModal(false);

    setInternalValue(newAddress.id);
    autoSelectDone.current = true;

    if (onChange) {
      const mockEvent = {
        target: {
          name,
          value: newAddress.id,
          _addressObject: newAddress
        }
      };
      onChange(mockEvent);
    }

    refetch();
  };

  return (
    <>
      <Dropdown
        title={label}
        value={internalValue}
        onChange={handleChange}
        options={addressOptions}
        error={error}
        required={required}
        placeholder={placeholder}
        maxWidth={fluid ? 'w-full' : 'max-w-xs'}
        className={className}
        disabled={disabled || isLoading}
      />

      {/* Semantic UI Modal for Address Form */}
      <Modal
        open={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        size="large"
        dimmer="blurring"
        closeIcon
      >
        <Modal.Header>Add New Address</Modal.Header>
        <Modal.Content scrolling style={{ maxHeight: '75vh' }}>
          <AddressForm
            onSuccess={handleAddressSuccess}
            onCancel={() => setShowAddressModal(false)}
            isInline={true}
          />
        </Modal.Content>
      </Modal>
    </>
  );
};

export default AddressDropdown;